/* eslint-disable react-hooks/exhaustive-deps */
import { IconMinus, IconPlus, IconX } from "@tabler/icons-react";
import SelectInput from "../../../components/Select";
import Input from "../../../components/Input";
import NewinputField from "../../../components/NewInput";
import { UseFormClearErrors } from "react-hook-form";
import { useProductAllListData } from "../../../apis/promoApis";

import { useEffect, useMemo, useState } from "react";
import DateRangeInput from "../../../components/common/DateRangeInput";
import ButtonStyled from "../../../components/common/ButtonStyled";

interface AssignDriversModalProps {
  open: boolean;
  onClose: () => void;
  setValue?: any;
  watch?: any;
  register?: any;
  errors?: any;
  clearErrors?: UseFormClearErrors<any>;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  loading?: boolean;
  mode: "add" | "view" | "edit";
  setMode?: (mode: "add" | "view" | "edit") => void;
  inline?: boolean;
  clients?: any;
  hideHeader?: boolean;
}

type DateRange = {
  startDate: string;
  endDate: string;
};

export default function CreatePromoModal({
  open,
  onClose,
  watch,
  setValue,
  register,
  errors,
  clearErrors,
  onSubmit,
  loading,
  mode,
  setMode,
  inline = false,
}: AssignDriversModalProps) {
  const [range, setRange] = useState<DateRange>({
    startDate: "",
    endDate: "",
  });
  const { data: products } = useProductAllListData();
  const productOptions = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    return products.flatMap((product: any) => {
      const hasVariants =
        Array.isArray(product?.variants) && product.variants.length > 0;
      if (hasVariants) {
        return product.variants.map((variant: any) => {
          const variantSku = variant?.sku || product?.sku || "";
          return {
            id: variant?.id || product?.id,
            value: variant?.id || product?.id,
            text: variantSku,
          };
        });
      }
      const parentSku =
        product?.sku ||
        (typeof product?.itemName === "object"
          ? product?.itemName?.en || product?.itemName?.ar || ""
          : product?.itemName || "");
      return [
        {
          id: product?.id,
          value: product?.id,
          text: parentSku,
        },
      ];
    });
  }, [products]);
  const [isEditable, setIsEditable] = useState(false);
  const watchedProducts = watch("products") || [];
  const [rows, setRows] = useState([{ id: Date.now(), value: "" }]);

  useEffect(() => {
    if (open && watchedProducts.length > 0) {
      setRows(
        watchedProducts.map((_: any, index: number) => ({
          id: Date.now() + index,
          value: "",
        }))
      );
    }
  }, [open, watchedProducts.length]);

  useEffect(() => {
    if (mode === "add" || mode === "edit") {
      setIsEditable(true);
      if (mode === "add" && (!watch("products") || watch("products").length === 0)) {
        setValue("products", [{ productId: "", quantity: "" }]);
      }
    }
    if (mode === "view") setIsEditable(false);
  }, [mode, open]);

  const isAdd = mode === "add";
  const isView = mode === "view" && !isEditable;
  const isEdit = mode === "edit" || (mode === "view" && isEditable);

  const addRow = () => {
    const newRows = [...rows, { id: Date.now(), value: "" }];
    setRows(newRows);

    const currentProducts = watch("products") || [];
    setValue("products", [...currentProducts, { productId: "", quantity: "" }]);
  };

  const removeRow = (id: number, index: number) => {
    setRows((prev) => prev.filter((row, i) => i !== index));

    const currentProducts = watch("products") || [];
    const updatedProducts = currentProducts.filter(
      (_: any, i: number) => i !== index
    );
    setValue("products", updatedProducts);
  };

  useEffect(() => {
    if (open) {
      const dateRange = watch("startEndDate");
      if (dateRange && typeof dateRange === "string") {
        const [startDate, endDate] = dateRange.split(" - ");
        setRange({ startDate, endDate });
      }
    }
  }, [open, watch("startEndDate")]);

  if (!open) return null;

  const formContent = (
    <form
      onSubmit={onSubmit}
      className={`relative  flex flex-col ${
        inline
          ? "bg-transparent w-full overflow-visible"
          : "bg-transparent w-[650px] overflow-hidden"
      }`}
    >
      {/* Header */}
      {/* <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-[#212121]">
          {isAdd && "Add Promo"}
          {isView && "View Promo"}
          {isEdit && "Edit Promo"}
        </h2>
        <div className="flex items-center gap-2">
          {isView && (
            <ButtonStyled
              type="button"
              onClick={() => setIsEditable(true)}
              variant="outline"
              value="Edit"
            />
          )}

          {(isAdd || isEdit) && (
            <ButtonStyled
              type="submit"
              disabled={loading}
              isPending={loading}
              variant="outline"
              value={isAdd ? "Save" : "Update"}
            />
          )}

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors cursor-pointer text-slate-700"
            title="Close"
          >
            <IconX size={18} />
          </button>
        </div>
      </div> */}

      {/* Content */}
      <div
        className={`flex-1 px-6 py-6 space-y-4 ${
          inline ? "overflow-visible" : "max-h-[75vh] overflow-y-auto"
        }`}
      >
        <div className="grid grid-cols-2 gap-4">
          <DateRangeInput
            title="Start & Exp. Date"
            name="startEndDate"
            disabled={!isEditable}
            isEditable={isEditable}
            register={register}
            error={errors?.startEndDate}
            validation={{ required: "Start & Exp. Date required" }}
            inputClassName="px-4 py-2 text-sm w-full rounded-xl bg-yellow-50 border border-[#FBD037]"
            value={range}
            onChange={(newRange) => {
              setRange(newRange);
              const formatted = `${newRange.startDate} - ${newRange.endDate}`;
              setValue("startEndDate", formatted, { shouldValidate: true });
              clearErrors?.("startEndDate");
            }}
          />

          <NewinputField
            name="promoCode"
            type="text"
            disabled={!isEditable}
            title="Promo Code"
            register={register}
            error={errors?.promoCode}
            validation={{
              required: "Promo Code is required",
              pattern: {
                value: /^[A-Za-z0-9]+$/,
                message:
                  "Promo Code must be alphanumeric with no spaces or special characters",
              },
            }}
          />

          {rows.map((row, index) => (
            <div key={row.id} className="flex items-end gap-2 col-span-2">
              {isEditable ? (
                <SelectInput
                  name={`products[${index}].productId`}
                  disabled={!isEditable}
                  value={watch(`products[${index}].productId`) || ""}
                  label="Select Product"
                  placeholder="Select Product"
                  selectOptions={productOptions}
                  onChange={(val) =>
                    setValue(`products[${index}].productId`, val)
                  }
                  register={register}
                  error={errors?.products?.[index]?.productId}
                  validation={{ required: "Please select a product" }}
                  className="w-full"
                />
              ) : (
                <Input
                  title="Product"
                  className="w-full"
                  name={`products[${index}].productId`}
                  disabled={true}
                  value={
                    productOptions.find(
                      (opt: any) => opt.value === watch(`products[${index}].productId`)
                    )?.text ||
                    products?.find(
                      (product: any) =>
                        product.itemName === watch(`products[${index}].productName`) ||
                        product.id === watch(`products[${index}].productId`) ||
                        product.sku === watch(`products[${index}].productId`)
                    )?.sku ||
                    watch(`products[${index}].productName`) ||
                    watch(`products[${index}].productId`) ||
                    ""
                  }
                  inputClassName="px-4 py-2 text-sm w-full rounded-xl outline-none opacity-70 bg-yellow-50/50 border border-[#FBD037] text-gray-700 cursor-not-allowed"
                />
              )}

              <NewinputField
                name={`products[${index}].quantity`}
                disabled={!isEditable}
                type="number"
                title="Quantity"
                register={register}
                error={errors?.products?.[index]?.quantity}
                validation={{
                  required: "Quantity is required",
                  min: {
                    value: 1,
                    message: "Quantity must be at least 1",
                  },
                }}
                other={{ min: 1 }}
                className="w-44"
              />

              {index === rows.length - 1 ? (
                <button
                  disabled={
                    !isEditable ||
                    !watch(`products[${index}].productId`) ||
                    !watch(`products[${index}].quantity`)
                  }
                  type="button"
                  onClick={addRow}
                  className={`flex-shrink-0 w-8 h-8 flex items-center justify-center 
      bg-[#FBD037]/20 text-black rounded-lg hover:bg-[#FBD037] 
      border border-[#FBD037] transition ${
        isEditable &&
        watch(`products[${index}].productId`) &&
        watch(`products[${index}].quantity`)
          ? ""
          : "opacity-70 cursor-not-allowed"
      }`}
                >
                  <IconPlus size={18} />
                </button>
              ) : (
                <button
                  disabled={!isEditable}
                  type="button"
                  onClick={() => removeRow(row.id, index)}
                  className={`flex-shrink-0 w-8 h-8 flex items-center justify-center 
      bg-red-100 text-red-600 rounded-lg hover:bg-red-200 
      border border-red-400 transition ${
        isEditable ? "" : "opacity-70 cursor-not-allowed"
      }`}
                >
                  <IconMinus size={18} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </form>
  );

  if (inline) {
    return formContent;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      {formContent}
    </div>
  );
}
